import type { ProcessTaskStatus } from '@common/enums';
import type { CustomControlData, IProcessAction } from '@common/interfaces';
import type {
    GetAvailableActionsQuery,
    GetProcessActivitiesResponseBody,
    GetProcessActorsResponseBody,
    GetProcessAuthorizationsResponseBody,
    GetProcessQuestionnaireResponsesResponseBody,
    GetServiceActorsQuery,
    GetServiceActorsResponseBody,
    HolterAdminReadData,
    IGetProcessActivitiesQuery,
    IGetProcessActorsQuery,
    IGetProcessQuestionnaireResponsesQuery,
    UpdateProcessActorsRequestBody,
    UpdateProcessRequestBody,
    UpdateProcessWorkflowStepsWithActivitiesAndTasksRequestBody,
} from '@common/interfaces/api/processesApi';
import type { GetProcessTasksResponseBody, ProcessTask } from '@common/interfaces/api/processTasksApi';
import type { IDiaryValidated } from '@common/validation/holterDiary';
import { fetchJson } from '@frontend/store/fetch';
import { getUrlQueryParameters } from './queryParameterUtils';

const baseUrl = '/api/processes';

export async function getProcessActivities<T = CustomControlData>(
    processId: string,
    query: IGetProcessActivitiesQuery | undefined,
    signal: AbortSignal
): Promise<GetProcessActivitiesResponseBody<T>> {
    const url = getUrlQueryParameters<IGetProcessActivitiesQuery>(`${baseUrl}/${processId}/activities`, query);
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function getProcessActors(
    processId: string,
    query: IGetProcessActorsQuery | undefined,
    signal: AbortSignal
): Promise<GetProcessActorsResponseBody> {
    const url = getUrlQueryParameters<IGetProcessActorsQuery>(`${baseUrl}/${processId}/actors`, query);

    const result = await fetchJson(url, 'GET');

    return result.json;
}

export async function updateProcessActors(
    processId: string,
    body: UpdateProcessActorsRequestBody,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${processId}/actors`;
    await fetchJson({ body, method: 'PUT', signal, url });
}

export async function updateProcess(
    processId: string,
    body: UpdateProcessRequestBody,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${processId}`;
    await fetchJson({ body, method: 'PATCH', signal, url });
}

export async function getProcessAuthorizations(
    processId: string,
    signal: AbortSignal
): Promise<GetProcessAuthorizationsResponseBody> {
    const url = `${baseUrl}/${processId}/authorizations`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function getProcessQuestionnaireResponses(
    processId: string,
    query: IGetProcessQuestionnaireResponsesQuery | undefined,
    signal: AbortSignal
): Promise<GetProcessQuestionnaireResponsesResponseBody> {
    const url = getUrlQueryParameters<IGetProcessQuestionnaireResponsesQuery>(
        `${baseUrl}/${processId}/questionnaireResponses`,
        query
    );

    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function getServiceActors(
    processId: string,
    query: GetServiceActorsQuery | undefined,
    signal: AbortSignal
): Promise<GetServiceActorsResponseBody> {
    const url = getUrlQueryParameters<GetServiceActorsQuery>(`${baseUrl}/${processId}/serviceActors`, query);
    const result = await fetchJson({ method: 'GET', signal, url });

    return result?.json;
}

export async function getAvailableActions(
    processId: string,
    query: GetAvailableActionsQuery | undefined,
    signal: AbortSignal
): Promise<IProcessAction[]> {
    const url = getUrlQueryParameters<GetAvailableActionsQuery>(`${baseUrl}/${processId}/availableActions`, query);
    const result = await fetchJson<IProcessAction[]>({ allowedHttpErrorCodes: [403], method: 'GET', signal, url });

    if (result.status === 403) {
        return [];
    }

    return result.json ?? [];
}

export async function getCanTakeOverTasksFromColleague(processId: string, signal: AbortSignal): Promise<boolean> {
    const url = `${baseUrl}/${processId}/canTakeOverTasksFromColleague`;
    const result = await fetchJson<boolean>({ method: 'GET', signal, url });

    return result.json ?? false;
}

export async function takeOverTasksFromColleague(processId: string, signal: AbortSignal): Promise<void> {
    const url = `${baseUrl}/${processId}/takeOverTasksFromColleague`;
    await fetchJson({ method: 'POST', signal, url });
}

export async function cancelProcess(processId: string, signal: AbortSignal): Promise<void> {
    const url = `${baseUrl}/${processId}/cancel`;
    await fetchJson({ method: 'POST', signal, url });
}

export async function getTasks(
    processId: string,
    status: (keyof typeof ProcessTaskStatus)[] | undefined,
    signal: AbortSignal
): Promise<ProcessTask[]> {
    let url = `${baseUrl}/${processId}/tasks`;
    if (status) {
        url += '?status=' + status.join('&status=');
    }
    const result = await fetchJson<GetProcessTasksResponseBody>({ method: 'GET', signal, url });
    const tasks = result.json;

    return tasks?.items ?? [];
}

export async function updateProcessWorkflowStepsWithActivitiesAndTasks(
    processId: string,
    body: UpdateProcessWorkflowStepsWithActivitiesAndTasksRequestBody
): Promise<void> {
    await fetchJson(
        `/api/admin/processes/${processId}/updateProcessWorkflowStepsWithActivitiesAndTasks`,
        'PATCH',
        body
    );
}

export async function createHolterDiaryForProcess(processId: string, body: IDiaryValidated): Promise<string> {
    const result = await fetchJson({
        body,
        method: 'POST',
        url: `/api/processes/${processId}/holterDiary`,
    });

    return result.json.processActivityId;
}

export async function getHolterAdminProcessData(processId: string): Promise<HolterAdminReadData> {
    const result = await fetchJson({
        method: 'GET',
        url: `/api/processes/${processId}/holterAdminProcessData`,
    });

    return result.json.holterAdminReadData;
}
